import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { AttendanceShiftLocationTypeEnumValues } from 'generated/resources/datatable.graphql'
import {
  AttendanceEnumsShiftSourceEnum,
  ClockInWidgetEmployeesSettingFragment,
  TimesheetOpenShiftFragment,
  TimesheetPageLastWorkingShiftFragment,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import moment from 'moment'
import { DeepPartial } from 'utility-types'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { TrackingProperties, track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import hasFeature from 'modules/core/lib/hasFeature'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'
import app from 'modules/core/store/models/app'

import EmployeePulseQuestion from 'modules/attendance/components/ClockIn/Tracker/Clockin/EmployeePulseQuestion'
import { ProjectClockInData } from 'modules/attendance/components/ClockIn/Tracker/tracker'
import { useShiftActions } from 'modules/attendance/hooks/shifts/useShiftActions'
import { AttendanceBreakConfigurations } from 'modules/attendance/hooks/useGetAttendanceBreakConfigurationsRange'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'
import OpenShiftUtils from 'modules/attendance/lib/utils/OpenShiftUtils'
import AttendanceOvertimeUtils from 'modules/attendance/lib/utils/OvertimeRequest'
import BreakSettingsUtils from 'modules/attendance/lib/utils/BreakSettings'
import validateBreakDurationRules, {
  ValidationError,
  isBreakSetting,
} from 'modules/attendance/lib/validateShiftBreakDurationRules'
import type { ActionType } from 'modules/attendance/store/models/Shift'
import { ActionFrom, ActionTypes, LOCATION_TYPES } from 'modules/attendance/store/models/Shift'

import ClockOutReminderModal, { CheckClockOutProps } from './ClockOutReminderModal'
import FinishAutoBreakModal from './FinishAutoBreakModal'
import useData from './withHooks/useData'

type PartialOpenShift = DeepPartial<TimesheetOpenShiftFragment> | null | undefined

type BreakSetting = NonNullable<
  NonNullable<ClockInWidgetEmployeesSettingFragment['attendanceBreakSetting']>
>

export interface RenderProps {
  loading: boolean
  clockedIn: boolean
  breakStarted: boolean
  hasAutomaticBreaks: boolean
  hasRestTime: boolean
  lastShiftLocationType?: string
  trackedDurationToday: number
  breakType: AttendanceBreakConfigurations | undefined
  openShift?: TimesheetOpenShiftFragment | null
  openShiftDuration: number
  onToggle: (
    action: ActionType,
    locationType?: string,
    attendanceBreak?: AttendanceBreakConfigurations | undefined,
    projectData?: ProjectClockInData
  ) => void
  today: moment.Moment
  employeeMissingClockOut: boolean
  projectClockInData: ProjectClockInData
  exceedsEstimatedThreshold: (shiftDuration?: number) => boolean
  restTimeAlertInconsistencies: ValidationError[]
  todayShifts: TimesheetPageShiftFragment[]
  maxMinutesPerDay: number
  maxRegularMinutes: number
  hasProjectEnabled: boolean
  lastWorkingShift?: TimesheetPageLastWorkingShiftFragment | null
  breaksEnabled: boolean
  desktopClockIn: boolean
  mobileClockIn: boolean
  canSeeOwnedAttendance: boolean
  breakConfigurations: AttendanceBreakConfigurations[]
  breakTypes: {
    id: string
    name: string
    isPaid: boolean
  }[]
  enableClockInLocation: boolean
  breakSetting: BreakSetting | null
}

interface ToggleProps {
  action: ActionType
  estimatedClockOutTime?: moment.Moment
  from?: string
  locationType?: string
  attendanceBreak?: AttendanceBreakConfigurations | undefined
  projectData?: ProjectClockInData
}

interface ShiftToggleProps {
  now: string
  source?: AttendanceEnumsShiftSourceEnum
  date: string
  locationType?: string
  timeSettingsBreakConfigurationId?: number
  projectWorkerId?: number | null
  subprojectId?: number | null
}

interface ClockInCheckerProps {
  children: (props: RenderProps) => React.ReactNode
}

export const shouldShowClockOutReminder = ({
  attendanceSetting,
  canSeeAttendance,
  showClockOutModal,
  showStillBreakModal,
  openShiftDuration,
  exceedsWithDuration,
  clockoutReminderFeatureEnabled,
}: {
  attendanceSetting: ClockInWidgetEmployeesSettingFragment
  canSeeAttendance: boolean
  showClockOutModal: boolean
  showStillBreakModal: boolean
  openShiftDuration: number
  exceedsWithDuration: (duration: number) => boolean
  clockoutReminderFeatureEnabled: boolean
}): boolean => {
  const clockOutReminderEnabled = EmployeesSettingsUtils.clockOutReminderEnabled(attendanceSetting)

  const enableReminder = clockoutReminderFeatureEnabled && clockOutReminderEnabled

  return (
    !showClockOutModal &&
    !showStillBreakModal &&
    canSeeAttendance &&
    enableReminder &&
    exceedsWithDuration(openShiftDuration)
  )
}

const COMPONENT_REFRESH_INTERVAL = 60 * 1000 // 60 sec

const ClockInChecker = ({ children }: ClockInCheckerProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { featureAvailability: hasProjectEnabled } = useAttendanceFeatures(
    Features.projects_management
  )
  const shiftActions = useShiftActions()

  const now = moment().unix()
  const [timestamp, setTimestamp] = useState(now)

  const {
    today,
    setToday,
    loading,
    openShift,
    lastWorkingShift,
    onRefresh,
    setLoading,
    breakSetting,
    checkRefreshData,
    attendanceSetting,
    overtimeRequest,
    breaksEnabled,
    breakDurationRules,
    todayShifts,
    estimatedTime,
    policies,
    desktopClockIn,
    mobileClockIn,
    canSeeOwnedAttendance,
    breakConfigurations,
    breakTypes,
  } = useData()

  // Modal visibility state
  const [showStillBreakModal, setShowStillBreakModal] = useState(false)
  const [showClockOutModal, setShowClockOutModal] = useState(false)
  const [showFinishAutoBreakModal, setShowFinishAutoBreakModal] = useState(false)
  const [showEmployeePulseQuestionModal, setShowEmployeePulseQuestionModal] = useState(false)

  const isShiftActive = !!openShift

  // Refresh interval effect
  React.useEffect(() => {
    const interval = setInterval(() => {
      if (isShiftActive) {
        setTimestamp(moment().unix())
      }
      checkRefreshData()
    }, COMPONENT_REFRESH_INTERVAL)

    return () => clearInterval(interval)
  }, [checkRefreshData, isShiftActive])

  // Visibility change effect
  React.useEffect(() => {
    document.addEventListener('visibilitychange', checkRefreshData)

    return () => {
      document.removeEventListener('visibilitychange', checkRefreshData)
    }
  }, [checkRefreshData])

  // Computed values
  const openShiftDuration =
    openShift?.clockIn && openShift?.date
      ? OpenShiftUtils.duration(openShift as TimesheetOpenShiftFragment)
      : 0

  const breakType = useMemo((): AttendanceBreakConfigurations | undefined => {
    if (openShift && openShift.timeSettingsBreakConfiguration) {
      const { name, id, paid } = openShift.timeSettingsBreakConfiguration

      if (name && id) {
        return {
          name,
          timeSettingsBreakConfigurationId: id,
          paid,
        }
      }
    }
    return undefined
  }, [openShift])

  const trackedMinutesToday = useMemo(() => {
    return (
      AttendanceShiftUtils.trackedMinutes(todayShifts) +
      AttendanceShiftUtils.trackedPaidBreakMinutes(todayShifts)
    )
  }, [todayShifts])

  const trackedMinutesTodayWithOpenShift =
    trackedMinutesToday + OpenShiftUtils.workableDuration(openShift)

  const lastShiftLocationType = useMemo((): string | undefined => {
    const { enableClockInLocation } = attendanceSetting || {}

    if (enableClockInLocation) return lastWorkingShift?.locationType || LOCATION_TYPES.OFFICE
    return undefined
  }, [lastWorkingShift, attendanceSetting])

  const projectClockInData = useMemo((): ProjectClockInData => {
    const { projectManagementTimeRecords } = lastWorkingShift || {}
    const timeRecords = projectManagementTimeRecords || []
    const currentDate = dateToString(new Date(), 'backend')

    let projectWorkerId, subprojectId

    if (lastWorkingShift?.referenceDate === currentDate) {
      projectWorkerId = timeRecords[0]?.projectWorker?.id
      subprojectId = timeRecords[0]?.subproject?.id
    }

    return { projectWorkerId, subprojectId }
  }, [lastWorkingShift])

  const exceedsWithDuration = useCallback(
    (duration: number): boolean => {
      const estimatedMinutes = estimatedTime.minutes
      return (
        trackedMinutesToday <= estimatedMinutes && trackedMinutesToday + duration > estimatedMinutes
      )
    },
    [trackedMinutesToday, estimatedTime.minutes]
  )

  const exceedsWithForgotClockOutDuration = useCallback(
    (
      duration: number,
      clockOutToleranceMinutes: number,
      overtimeRequestMinutes: number
    ): boolean => {
      const estimatedMinutes = estimatedTime.minutes
      const threshold = estimatedMinutes + clockOutToleranceMinutes + overtimeRequestMinutes

      return trackedMinutesToday + duration > threshold
    },
    [trackedMinutesToday, estimatedTime.minutes]
  )

  const exceedsEstimatedThreshold = useCallback(
    (shiftDuration = openShiftDuration || 0): boolean => {
      const missingClockOutEnabled =
        EmployeesSettingsUtils.missingClockOutEnabled(attendanceSetting)
      const forgotClockOutToleranceMinutes = attendanceSetting?.forgotClockOutToleranceMinutes

      return (
        missingClockOutEnabled &&
        exceedsWithForgotClockOutDuration(
          shiftDuration,
          forgotClockOutToleranceMinutes,
          AttendanceOvertimeUtils.minutes(overtimeRequest?.hoursAmountInCents || 0)
        )
      )
    },
    [openShiftDuration, attendanceSetting, overtimeRequest, exceedsWithForgotClockOutDuration]
  )

  const displayMissingClockOutView = useMemo((): boolean => {
    const canSeeAttendance = !!policies?.seeAttendance?.can

    return !!openShift && canSeeAttendance && exceedsEstimatedThreshold()
  }, [openShift, policies, exceedsEstimatedThreshold])

  const restTimeAlertInconsistencies = useMemo((): ValidationError[] => {
    if (isBreakSetting(breakDurationRules[0]) && breakDurationRules.length > 0) {
      if (BreakSettingsUtils.hasAutofixMissingBreaks(breakDurationRules[0])) return []
      return validateBreakDurationRules(todayShifts, breakDurationRules[0])
    }

    return []
  }, [breakDurationRules, todayShifts])

  // Helper functions
  const getEventName = useCallback((action: ActionType): string => {
    const { timeTracking } = trackingEvents

    switch (action) {
      case ActionTypes.CLOCK_IN_ACTION:
        return timeTracking.startClockIn
      case ActionTypes.CLOCK_OUT_ACTION:
        return timeTracking.startClockOut
      case ActionTypes.FORGOT_CLOCK_OUT_ACTION:
        return timeTracking.editClockOut
      case ActionTypes.BREAK_START_ACTION:
        return timeTracking.startBreak
      case ActionTypes.BREAK_END_ACTION:
        return timeTracking.startBackToWork
      default:
        return ''
    }
  }, [])

  const trackEvent = useCallback(
    (
      action: ActionType,
      from: string,
      locationType?: string,
      attendanceBreak?: AttendanceBreakConfigurations | undefined,
      projectData?: ProjectClockInData
    ) => {
      const pathname = window.location.href.includes('attendance') ? 'attendance' : 'dashboard'
      const { enableClockInLocation } = attendanceSetting || {}

      let trackParams: TrackingProperties = {
        page_path: pathname,
        from: from,
        location_type: locationType,
        wfh_enabled: enableClockInLocation,
      }

      if (hasFeature('time_tracking_paid_breaks') && !!attendanceBreak) {
        trackParams = {
          ...trackParams,
          break_type: attendanceBreak.paid ? 'paid' : 'unpaid',
          break_id: attendanceBreak.timeSettingsBreakConfigurationId,
        }
      }

      trackParams = {
        ...trackParams,
        projectChosen: !!projectData?.projectWorkerId,
        subProjectChosen: !!projectData?.subprojectId,
        newWidget: true,
        hasProjectsEnabled: hasProjectEnabled,
      }

      if (action === ActionTypes.CLOCK_IN_ACTION) {
        trackParams = {
          ...trackParams,
          resolutionWidth: document.body.clientWidth,
          resolutionHeight: document.body.clientHeight,
        }
      }

      track(getEventName(action), trackParams)
    },
    [attendanceSetting, hasProjectEnabled, getEventName]
  )

  const shiftAction = useCallback(
    async (
      action: ActionType,
      data: ShiftToggleProps
    ): Promise<UnhandledResult<Result<unknown>> | void> => {
      const { shiftClockIn, shiftClockOut, shiftForgotClockOut, shiftBreakStart, shiftBreakEnd } =
        shiftActions

      const locationType = AttendanceShiftLocationTypeEnumValues.find(
        (location) => location === data.locationType
      )

      const actionMap = {
        [ActionTypes.CLOCK_IN_ACTION]: () =>
          shiftClockIn({
            ...data,
            locationType: locationType,
          }),
        [ActionTypes.CLOCK_OUT_ACTION]: () =>
          shiftClockOut({
            ...data,
            endOn: dateToString(moment(), 'backend'),
            startOn: data.date,
          }),
        [ActionTypes.FORGOT_CLOCK_OUT_ACTION]: () => shiftForgotClockOut(data),
        [ActionTypes.BREAK_START_ACTION]: () =>
          shiftBreakStart({
            ...data,
            timeSettingsBreakConfigurationId: data.timeSettingsBreakConfigurationId,
            endOn: dateToString(moment(), 'backend'),
            startOn: data.date,
          }),
        [ActionTypes.BREAK_END_ACTION]: () =>
          shiftBreakEnd({
            ...data,
            endOn: dateToString(moment(), 'backend'),
            startOn: data.date,
            projectWorkerId: data.projectWorkerId,
            subprojectId: data.subprojectId,
          }),
      }

      return actionMap[action]?.() || Promise.resolve()
    },
    [shiftActions]
  )

  // Modal handlers
  const closeClockOutModals = useCallback(() => {
    setShowStillBreakModal(false)
    setShowClockOutModal(false)
  }, [])

  const closeFinishAutoBreakModal = useCallback(() => {
    setShowFinishAutoBreakModal(false)
  }, [])

  // Action handlers
  const onToggle = useCallback(
    async ({
      action,
      estimatedClockOutTime = moment(),
      from = ActionFrom.TIME_TRACKING_WIDGET,
      locationType,
      attendanceBreak,
      projectData,
    }: ToggleProps) => {
      setLoading(true)

      const { projectWorkerId, subprojectId } = projectData || {}

      let data: ShiftToggleProps = {
        now: estimatedClockOutTime.format(),
        date: openShift?.date ?? dateToString(estimatedClockOutTime, 'backend'),
        source: 'desktop',
        locationType: locationType,
        projectWorkerId: projectWorkerId,
        subprojectId: subprojectId,
      }

      if (hasFeature('time_tracking_paid_breaks') && !!attendanceBreak) {
        data = {
          ...data,
          timeSettingsBreakConfigurationId: Number(
            attendanceBreak?.timeSettingsBreakConfigurationId
          ),
        }
      }

      trackEvent(action, from, locationType, attendanceBreak, projectData)

      const unhandledResult = await shiftAction(action, data)

      let errorMessage = null
      if (unhandledResult) {
        handleResult(unhandledResult, (errrsHash) => (errorMessage = errrsHash.messages))
      }
      if (errorMessage) {
        app.setFlash({
          type: 'wrong',
          message: errorMessage,
        })

        setToday(null)
      } else {
        setToday(dateToString(moment().toDate(), 'backend'))
      }

      dispatch({
        type: EventTypes.ATTENDANCE_SHIFT_TOGGLED,
        data: { action },
      })
      setLoading(false)
      if (action === ActionTypes.CLOCK_OUT_ACTION) {
        setShowEmployeePulseQuestionModal(true)
      }
    },
    [openShift, setLoading, setToday, trackEvent, shiftAction]
  )

  const confirmClockOut = useCallback(
    (estimatedClockOutTime: moment.Moment, from: string, forgotClockOut: boolean) => {
      closeClockOutModals()

      const action = forgotClockOut
        ? ActionTypes.FORGOT_CLOCK_OUT_ACTION
        : ActionTypes.CLOCK_OUT_ACTION

      onToggle({ action, estimatedClockOutTime, from })
    },
    [closeClockOutModals, onToggle]
  )

  const workedMinutesCheckShowClockOutModal = useCallback((): boolean => {
    const canSeeAttendance = !!policies?.seeAttendance?.can
    const shouldShow = shouldShowClockOutReminder({
      attendanceSetting,
      canSeeAttendance,
      showClockOutModal,
      showStillBreakModal,
      openShiftDuration,
      exceedsWithDuration,
      clockoutReminderFeatureEnabled: hasFeature('clockout_reminder'),
    })

    if (shouldShow) {
      setShowClockOutModal(true)
      return true
    }

    return false
  }, [
    attendanceSetting,
    policies,
    showClockOutModal,
    showStillBreakModal,
    exceedsWithDuration,
    openShiftDuration,
  ])

  const checkClockOutToggle = useCallback(
    ({
      estimatedClockOutTime = moment(),
      from = ActionFrom.TIME_TRACKING_WIDGET,
      forgotClockOut = false,
    }: CheckClockOutProps) => {
      if (workedMinutesCheckShowClockOutModal()) return

      confirmClockOut(estimatedClockOutTime, from, forgotClockOut)
    },
    [workedMinutesCheckShowClockOutModal, confirmClockOut]
  )

  const confirmEndBreak = useCallback(async () => {
    closeFinishAutoBreakModal()
    onToggle({
      action: ActionTypes.BREAK_END_ACTION,
      locationType: lastShiftLocationType,
      projectData: projectClockInData,
    })
  }, [closeFinishAutoBreakModal, onToggle, lastShiftLocationType, projectClockInData])

  const onCheckToggle = useCallback(
    (
      action: ActionType,
      locationType?: string,
      attendanceBreak?: AttendanceBreakConfigurations | undefined,
      projectData?: ProjectClockInData
    ) => {
      if (action === ActionTypes.CLOCK_OUT_ACTION) {
        return checkClockOutToggle({})
      }

      if (action === ActionTypes.BREAK_END_ACTION) {
        // Check if openShift has the required fields for isAutomaticBreak
        if (
          openShift &&
          typeof openShift.workable === 'boolean' &&
          typeof openShift.automaticClockIn === 'boolean' &&
          OpenShiftUtils.isAutomaticBreak(openShift as TimesheetOpenShiftFragment)
        ) {
          return setShowFinishAutoBreakModal(true)
        }
      }

      onToggle({
        action,
        estimatedClockOutTime: moment(),
        locationType,
        attendanceBreak,
        projectData,
      })
    },
    [openShift, checkClockOutToggle, onToggle]
  )

  // Derived values for render
  const enableClockInLocation = !!attendanceSetting?.enableClockInLocation
  const clockedIn = !!openShift && !!openShift.workable && !displayMissingClockOutView
  const breakStarted = !!openShift && openShift.workable === false
  const hasAutomaticBreaks = breakSetting?.automaticBreaks ?? false
  const hasRestTime = !!breakSetting?.manualBreaks && !!breakSetting?.restTime

  return (
    <>
      {showClockOutModal && !!openShift && (
        <ClockOutReminderModal
          workedDurationToday={trackedMinutesTodayWithOpenShift}
          maxMinutesPerDay={estimatedTime.minutes}
          onClose={closeClockOutModals}
          onConfirm={checkClockOutToggle}
        />
      )}
      {showEmployeePulseQuestionModal && (
        <EmployeePulseQuestion
          estimatedTime={estimatedTime.minutes}
          workedTime={trackedMinutesToday}
          onClose={() => setShowEmployeePulseQuestionModal(false)}
        />
      )}
      {showFinishAutoBreakModal && !!openShift && (
        <FinishAutoBreakModal onClose={closeFinishAutoBreakModal} onConfirm={confirmEndBreak} />
      )}
      {children({
        today,
        loading,
        onToggle: onCheckToggle,
        clockedIn,
        breakStarted,
        projectClockInData,
        hasAutomaticBreaks,
        hasRestTime,
        lastShiftLocationType,
        // Cast to maintain backward compatibility - the original code also passed DeepPartial here
        openShift: openShift as TimesheetOpenShiftFragment | null | undefined,
        lastWorkingShift,
        enableClockInLocation,
        openShiftDuration,
        breakType,
        breakConfigurations,
        breakTypes,
        canSeeOwnedAttendance,
        trackedDurationToday: trackedMinutesTodayWithOpenShift,
        employeeMissingClockOut: displayMissingClockOutView,
        exceedsEstimatedThreshold,
        restTimeAlertInconsistencies,
        todayShifts,
        maxMinutesPerDay: estimatedTime.minutes,
        maxRegularMinutes: estimatedTime.regularMinutes,
        breaksEnabled,
        hasProjectEnabled,
        desktopClockIn,
        mobileClockIn,
        breakSetting,
      })}
    </>
  )
}

export default observer(ClockInChecker)
